import { useEffect, useRef, useCallback, useState } from 'react'
import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { API_BASE_URL } from '@/api/config'
import { SESSION_RESTORED_EVENT } from '@/api/client'
import { authenticatedFetch, consumeSse, isRecord, parseSseJson } from '@/api/streaming'
import { useQueryClient } from '@tanstack/react-query'
import { setDeviceStatus, trackDeviceEvent } from '@/lib/deviceDiagnostics'
import type {
  NewOrderEvent,
  OrderStatusEvent,
  DataChangedEvent,
  ConnectionStatus,
  RealtimeOrderBatch,
} from '../types'

interface UseOrderStreamOptions {
  restaurantId: number
  enabled?: boolean
  onNewOrder?: (event: NewOrderEvent) => void
  onOrderStatus?: (event: OrderStatusEvent) => void
  onDataChanged?: (event: DataChangedEvent) => void
  onConnectionChange?: (status: ConnectionStatus) => void
}

interface UseOrderStreamReturn {
  connectionStatus: ConnectionStatus
  reconnect: () => void
  disconnect: () => void
}

const RECONNECT_DELAY_MS = 3000
const MAX_RECONNECT_DELAY_MS = 60_000
// Attempts after which we surface 'error' and start fallback polling.
// Reconnect attempts continue forever regardless (kiosk tablets must self-heal).
const ERROR_STATUS_AFTER_ATTEMPTS = 5
// iOS routes fetch through CapacitorHttp, which only returns once the response has ended, so an
// endless SSE response never arrives there. The iPhone app polls instead and counts as connected.
// ponytail: up to 30 s delay on iPhone; a native SSE plugin (or WKAppBoundDomains + direct fetch) if that is too slow.
const POLL_ONLY = Capacitor.getPlatform() === 'ios'
const FALLBACK_POLL_INTERVAL_MS = POLL_ONLY ? 30_000 : 5 * 60 * 1000
// Server sends a heartbeat event every 30s; treat the connection as dead if
// nothing (events or heartbeats) arrived within 2.5x that period.
const HEARTBEAT_TIMEOUT_MS = 75_000
const WATCHDOG_INTERVAL_MS = 30_000
const RECONCILE_INTERVAL_MS = POLL_ONLY ? 30_000 : 60_000
const ORDER_CURSOR_STORAGE_PREFIX = 'admin-realtime-last-order'
const ALERTED_ORDERS_STORAGE_PREFIX = 'admin-realtime-alerted-orders'
const MAX_ALERTED_ORDERS = 300

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function isOrderType(value: unknown): value is NewOrderEvent['orderType'] {
  return value === 'delivery' || value === 'pickup' || value === 'dineIn'
}

export function isNewOrderEvent(value: unknown): value is NewOrderEvent {
  return (
    isRecord(value) &&
    isFiniteNumber(value.orderId) &&
    isFiniteNumber(value.restaurantId) &&
    isFiniteNumber(value.total) &&
    isOrderType(value.orderType) &&
    typeof value.status === 'string' &&
    typeof value.telephone === 'string' &&
    isFiniteNumber(value.timestamp)
  )
}

export function isOrderStatusEvent(value: unknown): value is OrderStatusEvent {
  return (
    isRecord(value) &&
    isFiniteNumber(value.orderId) &&
    isFiniteNumber(value.restaurantId) &&
    typeof value.status === 'string' &&
    isFiniteNumber(value.timestamp)
  )
}

export function isRealtimeOrderBatch(value: unknown): value is RealtimeOrderBatch {
  return (
    isRecord(value) &&
    Array.isArray(value.events) &&
    value.events.every(isNewOrderEvent) &&
    isFiniteNumber(value.latestOrderId) &&
    typeof value.hasMore === 'boolean'
  )
}

function loadOrderCursor(restaurantId: number): number {
  try {
    const value = localStorage.getItem(`${ORDER_CURSOR_STORAGE_PREFIX}:${restaurantId}`)
    const parsed = value === null ? 0 : Number(value)
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : 0
  } catch {
    return 0
  }
}

function saveOrderCursor(restaurantId: number, orderId: number) {
  try {
    localStorage.setItem(`${ORDER_CURSOR_STORAGE_PREFIX}:${restaurantId}`, String(orderId))
  } catch {
    // Reconciliation still works for the lifetime of this page when storage is unavailable.
  }
}

// Paid orders become visible out of id order (a later cash order can arrive while
// a PayPal order is still being paid), so dedupe alerts by id, not by cursor.
function loadAlertedOrderIds(restaurantId: number): Set<number> {
  try {
    const stored = JSON.parse(
      localStorage.getItem(`${ALERTED_ORDERS_STORAGE_PREFIX}:${restaurantId}`) ?? '[]'
    ) as unknown
    return new Set(
      Array.isArray(stored)
        ? stored.filter((id: unknown): id is number => Number.isSafeInteger(id))
        : []
    )
  } catch {
    return new Set()
  }
}

/** Returns false when the order was already alerted; otherwise remembers it (bounded, oldest dropped). */
export function rememberAlertedOrder(alerted: Set<number>, orderId: number): boolean {
  if (alerted.has(orderId)) return false
  alerted.add(orderId)
  for (const oldest of alerted) {
    if (alerted.size <= MAX_ALERTED_ORDERS) break
    alerted.delete(oldest)
  }
  return true
}

function saveAlertedOrderIds(restaurantId: number, alerted: Set<number>) {
  try {
    localStorage.setItem(
      `${ALERTED_ORDERS_STORAGE_PREFIX}:${restaurantId}`,
      JSON.stringify(Array.from(alerted))
    )
  } catch {
    // Dedupe still works for the lifetime of this page when storage is unavailable.
  }
}

function isDataChangedDomain(value: unknown): value is DataChangedEvent['domain'] {
  return (
    value === 'opening_times' ||
    value === 'menu' ||
    value === 'general_settings' ||
    value === 'delivery' ||
    value === 'vouchers' ||
    value === 'holidays' ||
    value === 'orders' ||
    value === 'reservations'
  )
}

export function isDataChangedEvent(value: unknown): value is DataChangedEvent {
  return (
    isRecord(value) &&
    isFiniteNumber(value.restaurantId) &&
    isDataChangedDomain(value.domain) &&
    Array.isArray(value.resources) &&
    value.resources.every((resource): resource is string => typeof resource === 'string') &&
    isFiniteNumber(value.timestamp)
  )
}

/**
 * Hook for connecting to the SSE order stream.
 * Uses authenticated streaming fetch and handles:
 * - new-order / order-status / data-changed events
 * - heartbeat watchdog: detects dead-but-open connections (sleep, network
 *   switch, proxy idle timeout) and force-reconnects
 * - endless reconnection with capped exponential backoff
 * - polling fallback while the stream is down
 * - immediate reconnect on tab visibility, app resume, and session re-auth
 */
export function useOrderStream({
  restaurantId,
  enabled = true,
  onNewOrder,
  onOrderStatus,
  onDataChanged,
  onConnectionChange,
}: UseOrderStreamOptions): UseOrderStreamReturn {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected')
  const abortControllerRef = useRef<AbortController | null>(null)
  const streamOpenRef = useRef(false)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fallbackPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastEventAtRef = useRef<number>(0)
  const lastOrderIdRef = useRef(loadOrderCursor(restaurantId))
  const alertedOrderIdsRef = useRef(loadAlertedOrderIds(restaurantId))
  const activeRestaurantIdRef = useRef(restaurantId)
  const reconciliationInFlightRef = useRef<number | null>(null)
  // Device diagnostics: total reconnects and the current state's start, for the status snapshot.
  const sseStatusRef = useRef<{ state: ConnectionStatus; since: number; reconnects: number }>({
    state: 'disconnected',
    since: Date.now(),
    reconnects: 0,
  })
  const queryClient = useQueryClient()

  activeRestaurantIdRef.current = restaurantId

  // Keep the latest callbacks in a ref so connect() never depends on their
  // identity. Otherwise every parent re-render (e.g. react-router's navigate
  // changing on navigation) would tear down and reopen the SSE connection.
  const callbacksRef = useRef({ onNewOrder, onOrderStatus, onDataChanged, onConnectionChange })
  useEffect(() => {
    callbacksRef.current = { onNewOrder, onOrderStatus, onDataChanged, onConnectionChange }
  })

  const publishSseStatus = useCallback(() => {
    setDeviceStatus('sse', { ...sseStatusRef.current, fallbackPolling: fallbackPollRef.current !== null })
  }, [])

  const stopFallbackPolling = useCallback(() => {
    if (fallbackPollRef.current) {
      clearInterval(fallbackPollRef.current)
      fallbackPollRef.current = null
      trackDeviceEvent('sse.fallback_polling', { on: false })
      publishSseStatus()
    }
  }, [publishSseStatus])

  const invalidateFallbackQueries = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['restaurant-status', restaurantId] })
    void queryClient.invalidateQueries({ queryKey: ['orders', restaurantId] })
  }, [queryClient, restaurantId])

  // The server owns the cursor: it may hold it below a still-unpaid order.
  const setOrderCursor = useCallback((orderId: number) => {
    if (orderId !== lastOrderIdRef.current) {
      lastOrderIdRef.current = orderId
      saveOrderCursor(restaurantId, orderId)
    }
  }, [restaurantId])

  const notifyNewOrder = useCallback((event: NewOrderEvent, via: 'sse' | 'reconcile') => {
    if (!rememberAlertedOrder(alertedOrderIdsRef.current, event.orderId)) return
    trackDeviceEvent('order.received', {
      orderId: event.orderId,
      via,
      latencyMs: Math.max(0, Date.now() - event.timestamp),
    })
    saveAlertedOrderIds(restaurantId, alertedOrderIdsRef.current)
    callbacksRef.current.onNewOrder?.(event)
  }, [restaurantId])

  const reconcileOrders = useCallback(async () => {
    if (
      !enabled ||
      !restaurantId ||
      reconciliationInFlightRef.current === restaurantId
    ) {
      return
    }

    const targetRestaurantId = restaurantId
    reconciliationInFlightRef.current = targetRestaurantId
    try {
      let afterOrderId = lastOrderIdRef.current
      let hasMore = true
      let batches = 0

      while (hasMore && batches < 20) {
        const response = await authenticatedFetch(
          `${API_BASE_URL}/api/admin/restaurants/${restaurantId}/orders/realtime?afterOrderId=${afterOrderId}`,
          { method: 'GET', headers: { Accept: 'application/json' } }
        )
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const body: unknown = await response.json()
        // A restaurant switch may happen while the request is in flight. Its
        // result must never advance the newly selected restaurant's cursor.
        if (activeRestaurantIdRef.current !== targetRestaurantId) {
          return
        }
        if (!isRealtimeOrderBatch(body)) {
          throw new Error('Invalid realtime order reconciliation response')
        }

        for (const event of body.events) {
          notifyNewOrder(event, 'reconcile')
        }

        setOrderCursor(body.latestOrderId)
        afterOrderId = body.latestOrderId
        hasMore = body.hasMore
        batches++

        if (hasMore && body.events.length === 0) {
          throw new Error('Realtime order reconciliation did not advance its cursor')
        }
      }

      if (batches > 0) {
        invalidateFallbackQueries()
      }
    } catch (error) {
      console.warn('Order reconciliation failed:', error)
    } finally {
      if (reconciliationInFlightRef.current === targetRestaurantId) {
        reconciliationInFlightRef.current = null
      }
    }
  }, [enabled, invalidateFallbackQueries, notifyNewOrder, restaurantId, setOrderCursor])

  const startFallbackPolling = useCallback(() => {
    if (fallbackPollRef.current || !restaurantId) {
      return
    }

    invalidateFallbackQueries()
    fallbackPollRef.current = setInterval(
      invalidateFallbackQueries,
      FALLBACK_POLL_INTERVAL_MS
    )
    trackDeviceEvent('sse.fallback_polling', { on: true }, 'warn')
    publishSseStatus()
  }, [invalidateFallbackQueries, publishSseStatus, restaurantId])

  const updateConnectionStatus = useCallback((status: ConnectionStatus) => {
    setConnectionStatus(status)
    callbacksRef.current.onConnectionChange?.(status)
    if (sseStatusRef.current.state !== status) {
      sseStatusRef.current = { ...sseStatusRef.current, state: status, since: Date.now() }
      publishSseStatus()
    }
  }, [publishSseStatus])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    streamOpenRef.current = false
    stopFallbackPolling()
    updateConnectionStatus('disconnected')
  }, [stopFallbackPolling, updateConnectionStatus])

  const connect = useCallback(() => {
    if (!enabled || !restaurantId) {
      return
    }

    // Cancel any pending reconnect so a stale timer can't kill this connection
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    // Close existing stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    if (POLL_ONLY) {
      updateConnectionStatus('connected')
      startFallbackPolling()
      return
    }

    const controller = new AbortController()
    abortControllerRef.current = controller
    streamOpenRef.current = false
    updateConnectionStatus('connecting')
    const url = `${API_BASE_URL}/api/admin/restaurants/${restaurantId}/orders/stream`
    lastEventAtRef.current = Date.now()

    const scheduleReconnect = () => {
      reconnectAttemptsRef.current++
      sseStatusRef.current = { ...sseStatusRef.current, reconnects: sseStatusRef.current.reconnects + 1 }

      // Surface the error state and start polling after a few failures,
      // but never stop trying to reconnect — always-on devices must recover
      // without user interaction.
      if (reconnectAttemptsRef.current >= ERROR_STATUS_AFTER_ATTEMPTS) {
        updateConnectionStatus('error')
        startFallbackPolling()
      } else {
        updateConnectionStatus('connecting')
      }

      const delay = Math.min(
        RECONNECT_DELAY_MS * Math.pow(2, reconnectAttemptsRef.current - 1),
        MAX_RECONNECT_DELAY_MS
      )
      reconnectTimeoutRef.current = setTimeout(() => {
        connect()
      }, delay)
    }

    const handleStreamEvent = (eventType: string, dataText: string) => {
      lastEventAtRef.current = Date.now()

      if (eventType === 'heartbeat') {
        return
      }

      const parsed = parseSseJson(dataText)

      try {
        switch (eventType) {
          case 'new-order': {
            if (!isNewOrderEvent(parsed)) {
              console.error('Invalid new-order event payload:', parsed)
              break
            }
            notifyNewOrder(parsed, 'sse')
            void queryClient.invalidateQueries({ queryKey: ['orders', restaurantId] })
            break
          }
          case 'order-status': {
            if (!isOrderStatusEvent(parsed)) {
              console.error('Invalid order-status event payload:', parsed)
              break
            }
            const data = parsed
            callbacksRef.current.onOrderStatus?.(data)
            void queryClient.invalidateQueries({ queryKey: ['orders', restaurantId] })
            break
          }
          case 'data-changed': {
            if (!isDataChangedEvent(parsed)) {
              console.error('Invalid data-changed event payload:', parsed)
              break
            }
            const data = parsed
            callbacksRef.current.onDataChanged?.(data)
            break
          }
          default:
            console.warn('Unknown order stream event:', eventType)
        }
      } catch (error) {
        console.error(`Failed to parse ${eventType} event:`, error)
      }
    }

    const attempt = reconnectAttemptsRef.current
    const connectStartedAt = Date.now()
    let httpStatus: number | undefined
    trackDeviceEvent('sse.connecting', { attempt }, 'debug')

    void (async () => {
      try {
        const response = await authenticatedFetch(url, {
          method: 'GET',
          headers: { Accept: 'text/event-stream' },
          signal: controller.signal,
        })

        if (!response.ok) {
          httpStatus = response.status
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        trackDeviceEvent('sse.connected', { attempt, ms: Date.now() - connectStartedAt })
        reconnectAttemptsRef.current = 0
        lastEventAtRef.current = Date.now()
        streamOpenRef.current = true
        stopFallbackPolling()
        updateConnectionStatus('connected')
        invalidateFallbackQueries()
        void reconcileOrders()

        await consumeSse(response, handleStreamEvent, { signal: controller.signal })

        if (!controller.signal.aborted) {
          throw new Error('Order stream closed')
        }
      } catch (error) {
        if (controller.signal.aborted) {
          return
        }
        console.error('Order stream connection failed:', error)
        trackDeviceEvent(
          'sse.error',
          {
            attempt,
            ...(httpStatus !== undefined ? { httpStatus } : {}),
            error: error instanceof Error ? error.message : String(error),
          },
          'warn'
        )
        streamOpenRef.current = false
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null
        }
        scheduleReconnect()
      }
    })()
  }, [
    enabled,
    invalidateFallbackQueries,
    notifyNewOrder,
    queryClient,
    reconcileOrders,
    restaurantId,
    startFallbackPolling,
    stopFallbackPolling,
    updateConnectionStatus,
  ])

  // Pub/Sub and SSE are intentionally ephemeral. Reconcile against the shared
  // database periodically even while the stream appears healthy.
  useEffect(() => {
    if (!enabled || !restaurantId) return

    lastOrderIdRef.current = loadOrderCursor(restaurantId)
    alertedOrderIdsRef.current = loadAlertedOrderIds(restaurantId)
    void reconcileOrders()
    const interval = setInterval(() => void reconcileOrders(), RECONCILE_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [enabled, restaurantId, reconcileOrders])

  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0
    disconnect()
    connect()
  }, [connect, disconnect])

  // Connect/disconnect based on enabled state
  useEffect(() => {
    if (enabled && restaurantId) {
      connect()
    } else {
      disconnect()
    }

    return () => {
      disconnect()
    }
  }, [enabled, restaurantId, connect, disconnect])

  // Heartbeat watchdog: a stream can stay open on a dead TCP connection
  // (laptop sleep, network switch, LB idle timeout). If nothing has arrived
  // within the timeout window, force a reconnect.
  useEffect(() => {
    if (!enabled || !restaurantId) return

    const watchdog = setInterval(() => {
      if (!streamOpenRef.current) return
      const silentMs = Date.now() - lastEventAtRef.current
      if (silentMs > HEARTBEAT_TIMEOUT_MS) {
        trackDeviceEvent('sse.watchdog', { silentMs }, 'warn')
        reconnectAttemptsRef.current = 0
        connect()
      }
    }, WATCHDOG_INTERVAL_MS)

    return () => clearInterval(watchdog)
  }, [enabled, restaurantId, connect])

  // Reconnect immediately after the axios interceptor silently re-logs in so
  // the long-lived stream picks up the refreshed cookie/token immediately.
  useEffect(() => {
    if (!enabled || !restaurantId) return

    const handleSessionRestored = () => {
      reconnectAttemptsRef.current = 0
      connect()
    }
    window.addEventListener(SESSION_RESTORED_EVENT, handleSessionRestored)
    return () => window.removeEventListener(SESSION_RESTORED_EVENT, handleSessionRestored)
  }, [enabled, restaurantId, connect])

  // Reconnect on app resume (handles background suspension on mobile + tab visibility on web)
  useEffect(() => {
    if (!enabled || !restaurantId) return

    const checkAndReconnect = () => {
      // Reconnect when closed, or when open but stale (dead TCP connection
      // that the browser hasn't noticed yet).
      const isStale = Date.now() - lastEventAtRef.current > HEARTBEAT_TIMEOUT_MS
      if (!streamOpenRef.current || isStale) {
        reconnectAttemptsRef.current = 0
        connect()
      }
    }

    // Web + Capacitor WebView: fires when tab/app becomes visible
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkAndReconnect()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    // Capacitor-specific: catches native app state transitions that visibilitychange may miss
    let appStateListener: { remove: () => void } | null = null
    let disposed = false
    if (Capacitor.isNativePlatform()) {
      void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          checkAndReconnect()
        }
      }).then((listener) => {
        // The effect may have been cleaned up before the listener registration
        // resolved — remove it right away instead of leaking it.
        if (disposed) {
          void listener.remove()
        } else {
          appStateListener = listener
        }
      })
    }

    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', handleVisibility)
      appStateListener?.remove()
    }
  }, [enabled, restaurantId, connect])

  return {
    connectionStatus,
    reconnect,
    disconnect,
  }
}
